import { Fragment, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { NAVY } from '../utils/constants';
import { useCruiseSettings } from '../hooks/useCruiseSettings';
import { NoteStrip } from './ui';
import { RefreshCw, Hourglass, Phone, Mail, PhoneCall, UserPlus, AlertTriangle, EyeOff, ChevronDown, ChevronRight, CalendarPlus } from 'lucide-react';
import { CUSTOMER_WINDOW_DAYS } from '../utils/bookingWindow';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const relTime = (iso) => {
  if (!iso) return '';
  const s = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return h === 1 ? '1 hour ago' : `${h} hours ago`;
  const d = Math.floor(h / 24);
  return d === 1 ? 'yesterday' : `${d} days ago`;
};

// Recency color: the whole point of the list is that "20 minutes ago" and
// "3 days ago" read differently at a glance.
const relColor = (iso) => {
  const h = (Date.now() - new Date(iso)) / 3600000;
  if (h < 1)  return '#15803D';
  if (h < 24) return NAVY;
  return 'var(--color-ink-muted)';
};

// Hold dates are DD-MM-YYYY text; be defensive in case an ISO date appears.
const holdDateLabel = (d) => {
  if (!d) return '';
  const m = String(d).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : d;
};

const partyLabel = (r) => {
  const parts = [];
  if (r.adults)   parts.push(`${r.adults} adult${r.adults !== 1 ? 's' : ''}`);
  if (r.children) parts.push(`${r.children} child${r.children !== 1 ? 'ren' : ''}`);
  if (r.infants)  parts.push(`${r.infants} infant${r.infants !== 1 ? 's' : ''}`);
  return parts.join(' · ');
};

const STAGES = {
  paid_no_seat:    { label: 'Paid — seating failed', color: '#991B1B', bg: '#FEE2E2', border: '#FCA5A5' },
  reached_payment: { label: 'Reached payment',       color: '#92400E', bg: '#FEF3C7', border: '#FCD34D' },
  selected_cruise: { label: 'Selected cruise',       color: '#1E40AF', bg: '#DBEAFE', border: '#93C5FD' },
  logged_in:       { label: 'Logged in only',        color: '#4B5563', bg: '#F3F4F6', border: '#E5E7EB' },
};

const OUTCOMES = [
  { value: 'no_answer',      label: 'No answer',      color: '#4B5563', bg: '#F3F4F6', border: '#D1D5DB' },
  { value: 'call_back',      label: 'Call back',      color: '#92400E', bg: '#FEF3C7', border: '#FCD34D' },
  { value: 'interested',     label: 'Interested',     color: '#15803D', bg: '#F0FDF4', border: '#86EFAC' },
  { value: 'not_interested', label: 'Not interested', color: '#991B1B', bg: '#FEE2E2', border: '#FCA5A5' },
];
const outcomeMeta = (v) => OUTCOMES.find(o => o.value === v) || OUTCOMES[0];

const chip = (text, { color, bg, border }) => (
  <span style={{ display: 'inline-block', background: bg, color, border: `1px solid ${border}`, borderRadius: 6, padding: '2px 9px', fontSize: '0.68rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
    {text}
  </span>
);

// Staff identity from an email — enough to answer "who already called?".
const callerLabel = (email) => (email || '').split('@')[0] || 'unknown';

// Adding a lead writes its own call-log entry with this note. Nobody typed
// it and the row already shows a "Lead added" chip, so it is not shown as a
// call note.
const AUTO_LEAD_NOTE = 'Lead added — wants';

// ─── Duplicate detection ─────────────────────────────────────────────────────
// A "duplicate" is two live bookings for the same online guest (customer_id)
// on the SAME cruise date for the SAME cruise type. The same guest doing a
// sunset and a dinner cruise on one day, or returning a month later, is
// normal and must not be flagged. Phone-sharing is deliberately ignored —
// the match is on customer_id only.
//
// Flags are PAIRS (not groups) so each one can be dismissed on its own. The
// pair key is the two booking ids in sorted order — the same canonical form
// the duplicate_dismissals table stores.

const pairKey = (a, b) => (a < b ? `${a}|${b}` : `${b}|${a}`);

const findDuplicatePairs = (bookings) => {
  const groups = {};
  (bookings || []).forEach(b => {
    const k = `${b.customer_id}|${b.date || ''}|${(b.cruise_type || '').trim().toLowerCase()}`;
    (groups[k] = groups[k] || []).push(b);
  });
  const pairs = [];
  Object.values(groups).forEach(g => {
    if (g.length < 2) return;
    for (let i = 0; i < g.length; i++) {
      for (let j = i + 1; j < g.length; j++) {
        const [a, b] = g[i].id < g[j].id ? [g[i], g[j]] : [g[j], g[i]];
        pairs.push({ key: pairKey(a.id, b.id), a, b });
      }
    }
  });
  return pairs;
};

// Text snapshot stored with a dismissal so the "dismissed" list still reads
// well after the bookings change or disappear.
const pairSummary = ({ a, b }) => {
  const names = a.booking_name === b.booking_name ? a.booking_name : `${a.booking_name} / ${b.booking_name}`;
  const decks = [a.deck, b.deck].filter(Boolean);
  const deck = decks.length ? ` · ${[...new Set(decks)].join(' / ')}` : '';
  return `${names} · ${a.date} · ${a.cruise_type}${deck}`;
};

const channelLabel = (b) => ((b.booking_channel || '').startsWith('online') ? 'Online' : 'Internal');

// ─── Lead helpers ────────────────────────────────────────────────────────────
// online_enquiries.preferred_date is ISO (YYYY-MM-DD) text; hold dates here
// are DD-MM-YYYY text. Convert at the edges only.
const toIsoDate = (d) => {
  if (!d) return '';
  const s = String(d);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const m = s.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : '';
};
const isoToDisplay = (iso) => {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : (iso || '');
};
// Deck names on holds ('Upper Deck') → the enquiry codes the Enquiries screen
// labels ('upper' / 'middle' / 'none').
const deckCode = (deck) => {
  const s = String(deck || '').toLowerCase();
  if (s.includes('upper')) return 'upper';
  if (s.includes('middle')) return 'middle';
  return 'none';
};
// Online booking window: see src/utils/bookingWindow.js (mirror of the public
// repo's _shared/bookingWindow.ts). Used for the hint only; the gate is server-side.
const ONLINE_WINDOW_DAYS = CUSTOMER_WINDOW_DAYS;
const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const daysFromToday = (iso) => {
  const [y, m, d] = iso.split('-').map(Number);
  const [ty, tm, td] = todayIso().split('-').map(Number);
  return Math.round((Date.UTC(y, m - 1, d) - Date.UTC(ty, tm - 1, td)) / 86400000);
};
const openingDateIso = (iso) => {
  const [y, m, d] = iso.split('-').map(Number);
  const t = new Date(Date.UTC(y, m - 1, d - ONLINE_WINDOW_DAYS));
  return t.toISOString().slice(0, 10);
};

// ─── Add-lead modal ──────────────────────────────────────────────────────────
// A caller who is interested, but for a date online booking is not open for
// yet. Records the wanted date as an enquiry (source 'staff', tied to the
// online customer by customer_id) so every lead lives on the Enquiries
// screen. Sends nothing — Stage 3 adds the confirmation message.

function AddLeadModal({ lead, cruiseSettings, onClose, onSaved }) {
  const [date, setDate]         = useState(toIsoDate(lead.hold_date));
  const [cruise, setCruise]     = useState(lead.cruise_type || '');
  const [deck, setDeck]         = useState(deckCode(lead.deck));
  const [adults, setAdults]     = useState(lead.adults ?? 1);
  const [children, setChildren] = useState(lead.children ?? 0);
  const [infants, setInfants]   = useState(lead.infants ?? 0);
  const [note, setNote]         = useState('');
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');

  const activeCruises = (cruiseSettings || []).filter(s => s.is_active !== false);
  const dateOk = /^\d{4}-\d{2}-\d{2}$/.test(date);
  const days = dateOk ? daysFromToday(date) : null;
  const windowHint = !dateOk ? '' :
    days < 0 ? 'That date has already passed.' :
    days <= ONLINE_WINDOW_DAYS ? 'Online booking for this date is already open — they can book now.' :
    `Online booking for this date opens on ${isoToDisplay(openingDateIso(date))}.`;

  const save = async () => {
    if (!dateOk) { setError('Choose the date they want.'); return; }
    if (days < 0) { setError('That date has already passed.'); return; }
    if (!cruise) { setError('Choose the cruise they want.'); return; }
    if (!(Number(adults) > 0)) { setError('At least one adult.'); return; }
    setSaving(true);
    setError('');
    const { data: userData } = await supabase.auth.getUser();
    const addedBy = userData?.user?.email || 'unknown';
    const { error: err } = await supabase.from('online_enquiries').insert({
      full_name: lead.full_name || 'Unknown name',
      phone: (lead.phone || '').replace(/\D/g, '').slice(-10),
      preferred_date: date,
      cruise_preference: cruise,
      deck_preference: deck || null,
      adults: Number(adults) || 0,
      children: Number(children) || 0,
      infants: Number(infants) || 0,
      status: 'Contacted',
      source: 'staff',
      customer_id: lead.customer_id,
      added_by: addedBy,
      note: note.trim() || null,
    });
    if (err) {
      setSaving(false);
      setError(/column|schema cache/i.test(err.message)
        ? `The database is missing the lead columns — paste the Stage 1 migration in Supabase first. (${err.message})`
        : err.message);
      return;
    }
    // Reflect the call in the log so "who already rang" stays true. Non-fatal.
    await supabase.from('lead_followups').insert({
      customer_id: lead.customer_id,
      outcome: 'interested',
      note: `${AUTO_LEAD_NOTE} ${cruise} on ${isoToDisplay(date)}`,
    });
    setSaving(false);
    onSaved();
  };

  const field = { width: '100%', boxSizing: 'border-box', border: '1.5px solid var(--color-border-strong)', borderRadius: 8, padding: '8px 10px', fontSize: '0.84rem', fontFamily: 'inherit', color: NAVY, background: '#fff', outline: 'none' };
  const label = { display: 'block', fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', color: 'var(--color-ink-muted)', marginBottom: 4 };

  return (
    <div className="ss-modal-backdrop" onClick={onClose}>
      <div className="ss-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 800, color: NAVY, margin: '0 0 4px' }}>Add lead</h3>
        <div style={{ fontSize: '0.82rem', color: 'var(--color-ink-secondary)', marginBottom: 14 }}>
          {lead.full_name || 'Unknown name'}{lead.phone ? ` · ${lead.phone}` : ''}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
          <div>
            <label style={label}>Wanted date</label>
            <input type="date" value={date} min={todayIso()} onChange={e => setDate(e.target.value)}
              onClick={e => e.target.showPicker && e.target.showPicker()} style={{ ...field, colorScheme: 'light' }} />
          </div>
          <div>
            <label style={label}>Cruise</label>
            <select value={cruise} onChange={e => setCruise(e.target.value)} style={field}>
              <option value="">Select cruise…</option>
              {activeCruises.map(s => <option key={s.id} value={s.cruise_name}>{s.cruise_name}</option>)}
            </select>
          </div>
        </div>
        {windowHint && (
          <div style={{ fontSize: '0.76rem', color: days < 0 ? '#991B1B' : 'var(--color-ink-secondary)', marginBottom: 10 }}>{windowHint}</div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr 1fr', gap: 10, marginBottom: 10 }}>
          <div>
            <label style={label}>Deck</label>
            <select value={deck} onChange={e => setDeck(e.target.value)} style={field}>
              <option value="none">No preference</option>
              <option value="upper">Upper Deck</option>
              <option value="middle">Middle Deck</option>
            </select>
          </div>
          <div><label style={label}>Adults</label><input type="number" min={1} value={adults} onChange={e => setAdults(e.target.value)} style={field} /></div>
          <div><label style={label}>Children</label><input type="number" min={0} value={children} onChange={e => setChildren(e.target.value)} style={field} /></div>
          <div><label style={label}>Infants</label><input type="number" min={0} value={infants} onChange={e => setInfants(e.target.value)} style={field} /></div>
        </div>

        <label style={label}>Note</label>
        <textarea value={note} onChange={e => setNote(e.target.value)} rows={2}
          placeholder="What they said — occasion, flexibility on date…"
          style={{ ...field, resize: 'vertical' }} />

        <p style={{ margin: '10px 0 0', fontSize: '0.72rem', color: 'var(--color-ink-muted)' }}>
          The lead appears on the Enquiries screen under Contacted, and the call is logged as Interested.
          Nothing is sent to the guest yet.
        </p>

        {error && <p style={{ margin: '10px 0 0', fontSize: '0.78rem', color: '#991B1B' }}>{error}</p>}

        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} disabled={saving}
            style={{ padding: '8px 18px', background: 'var(--color-sunken)', color: NAVY, border: '1.5px solid var(--color-border-strong)', borderRadius: 8, fontWeight: 700, fontSize: '0.84rem', cursor: 'pointer' }}>
            Cancel
          </button>
          <button onClick={save} disabled={saving}
            style={{ padding: '8px 18px', background: NAVY, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: '0.84rem', cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Saving…' : 'Add lead'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Log-call modal ──────────────────────────────────────────────────────────

function LogCallModal({ lead, onClose, onSaved }) {
  const [outcome, setOutcome] = useState('no_answer');
  const [note, setNote]       = useState('');
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');

  const save = async () => {
    setSaving(true);
    const { error: err } = await supabase.from('lead_followups').insert({
      customer_id: lead.customer_id,
      outcome,
      note: note.trim() || null,
    });
    setSaving(false);
    if (err) { setError(err.message); return; }
    onSaved();
  };

  return (
    <div className="ss-modal-backdrop" onClick={onClose}>
      <div className="ss-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 800, color: NAVY, margin: '0 0 4px' }}>Log a call</h3>
        <div style={{ fontSize: '0.82rem', color: 'var(--color-ink-secondary)', marginBottom: 14 }}>
          {lead.full_name || 'Unknown name'}{lead.phone ? ` · ${lead.phone}` : ''}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
          {OUTCOMES.map(o => (
            <button key={o.value} onClick={() => setOutcome(o.value)}
              style={{
                padding: '9px 10px', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: '0.8rem',
                background: outcome === o.value ? o.bg : '#fff',
                color: o.color,
                border: `1.5px solid ${outcome === o.value ? o.color : 'var(--color-border-strong)'}`,
              }}>
              {o.label}
            </button>
          ))}
        </div>

        <textarea
          value={note} onChange={e => setNote(e.target.value)} rows={3}
          placeholder="Note (optional) — what was said, when to call back…"
          style={{ width: '100%', boxSizing: 'border-box', border: '1.5px solid var(--color-border-strong)', borderRadius: 8, padding: '9px 12px', fontSize: '0.84rem', fontFamily: 'inherit', resize: 'vertical', outline: 'none', color: NAVY }}
        />

        <p style={{ margin: '10px 0 0', fontSize: '0.72rem', color: 'var(--color-ink-muted)' }}>
          Entries can't be edited or deleted — the log is an audit trail. Logged the wrong outcome?
          Just log another call; the newest entry is what everyone sees.
        </p>

        {error && <p style={{ margin: '10px 0 0', fontSize: '0.78rem', color: '#991B1B' }}>{error}</p>}

        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} disabled={saving}
            style={{ padding: '8px 18px', background: 'var(--color-sunken)', color: NAVY, border: '1.5px solid var(--color-border-strong)', borderRadius: 8, fontWeight: 700, fontSize: '0.84rem', cursor: 'pointer' }}>
            Cancel
          </button>
          <button onClick={save} disabled={saving}
            style={{ padding: '8px 18px', background: NAVY, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: '0.84rem', cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Saving…' : 'Log call'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function IncompleteBookings({ onNavigate }) {
  const { settings: cruiseSettings } = useCruiseSettings();
  const [rows, setRows]       = useState(null);   // null = not yet loaded
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [logLead, setLogLead] = useState(null);   // lead being call-logged
  const [addLead, setAddLead] = useState(null);   // lead being recorded as an enquiry
  const [justLogged, setJustLogged] = useState('');
  const [leadByCustomer, setLeadByCustomer] = useState({}); // customer_id → open staff enquiry
  const [callNotes, setCallNotes]   = useState({});   // customer_id → typed call notes, newest first
  const [leadNotes, setLeadNotes]   = useState({});   // customer_id → newest enquiry carrying a note
  const [openNotes, setOpenNotes]   = useState({});   // customer_id → earlier call notes expanded
  const [dupPairs, setDupPairs]     = useState([]);   // flagged pairs not yet dismissed
  const [dismissed, setDismissed]   = useState([]);   // duplicate_dismissals rows, newest first
  const [dismissMissing, setDismissMissing] = useState(false); // table not created yet
  const [showDismissed, setShowDismissed]   = useState(false);
  const [dismissing, setDismissing]   = useState('');  // pair key being saved
  const [dismissError, setDismissError] = useState('');

  const fetchRows = useCallback(async () => {
    setLoading(true);
    const [{ data, error: err }, { data: leads }, { data: dupBkgs }, { data: dis, error: disErr }, { data: followupNotes }, { data: notedLeads }] = await Promise.all([
      supabase.rpc('get_incomplete_checkouts'),
      // Open leads already recorded from this screen, so the row shows
      // "Lead added" instead of offering it twice. Errors (e.g. the Stage 1
      // columns not yet applied) just leave the map empty.
      supabase.from('online_enquiries')
        .select('id, customer_id, preferred_date, cruise_preference, status')
        .not('customer_id', 'is', null)
        .neq('status', 'Closed')
        .is('converted_booking_id', null),
      // Live bookings for online guests (customer_id set). Grouping into
      // same-date, same-cruise pairs happens in findDuplicatePairs.
      supabase.from('bookings')
        .select('id, customer_id, booking_name, date, cruise_type, deck, payment_status, booking_channel')
        .not('customer_id', 'is', null)
        .neq('payment_status', 'Cancelled'),
      supabase.from('duplicate_dismissals')
        .select('id, booking_a, booking_b, summary, dismissed_by, created_at')
        .order('created_at', { ascending: false }),
      // Notes typed when logging a call. The RPC returns only the last call's
      // outcome, so the notes are read from the log itself (staff can select
      // it). Errors just leave the rows without notes.
      supabase.from('lead_followups')
        .select('id, customer_id, note, called_by, outcome, created_at')
        .not('note', 'is', null)
        .order('created_at', { ascending: false })
        .limit(1000),
      // The note on a lead added from this screen — closed leads included:
      // what the guest said is still worth knowing before the next call.
      supabase.from('online_enquiries')
        .select('*')
        .not('customer_id', 'is', null)
        .not('note', 'is', null)
        .order('created_at', { ascending: false }),
    ]);
    setLoading(false);
    if (err) { setError(err); return; }
    setError(null);
    setRows(data || []);
    // Missing table (SQL not run yet) must not break the page: the panel still
    // flags pairs, only the dismiss action is unavailable.
    const tableMissing = !!disErr && (disErr.code === '42P01' || disErr.code === 'PGRST205');
    setDismissMissing(tableMissing);
    const dismissedRows = tableMissing || disErr ? [] : (dis || []);
    setDismissed(dismissedRows);
    const dismissedKeys = new Set(dismissedRows.map(d => pairKey(d.booking_a, d.booking_b)));
    setDupPairs(findDuplicatePairs(dupBkgs).filter(p => !dismissedKeys.has(p.key)));
    const byCust = {};
    (leads || []).forEach(l => { if (!byCust[l.customer_id]) byCust[l.customer_id] = l; });
    setLeadByCustomer(byCust);
    const notesByCust = {};
    (followupNotes || []).forEach(f => {
      const text = (f.note || '').trim();
      if (!text || text.startsWith(AUTO_LEAD_NOTE)) return;
      (notesByCust[f.customer_id] = notesByCust[f.customer_id] || []).push(f);
    });
    setCallNotes(notesByCust);
    const leadNoteByCust = {};
    (notedLeads || []).forEach(l => {
      if ((l.note || '').trim() && !leadNoteByCust[l.customer_id]) leadNoteByCust[l.customer_id] = l;
    });
    setLeadNotes(leadNoteByCust);
  }, []);

  const dismissPair = async (pair) => {
    setDismissing(pair.key);
    setDismissError('');
    const { error: insErr } = await supabase.from('duplicate_dismissals').insert({
      booking_a: pair.a.id,
      booking_b: pair.b.id,
      summary: pairSummary(pair),
    });
    setDismissing('');
    // 23505 = already dismissed by someone else meanwhile — treat as done.
    if (insErr && insErr.code !== '23505') { setDismissError(insErr.message); return; }
    fetchRows();
  };

  useEffect(() => { fetchRows(); }, [fetchRows]);
  useEffect(() => {
    const onFocus = () => fetchRows();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [fetchRows]);

  const convertLead = (r) => {
    // Reuse the standard Add Inquiry flow (validation, pricing, closure check,
    // duplicate modal, Pending default). No seating happens on save, so no
    // inventory is consumed until a manager actually seats the party.
    // bookings has no guest_email column (see GAPS.md) — carry it in notes.
    const noteParts = ['Online lead follow-up'];
    if (r.email) noteParts.push(`email: ${r.email}`);
    onNavigate('add-inquiry-prefill', r.hold_date ? holdDateLabel(r.hold_date) : '', {
      booking_name: r.full_name || '',
      guest_phone: (r.phone || '').replace(/\D/g, '').slice(-10),
      adults: r.adults ?? '',
      children: r.children ?? 0,
      infants: r.infants ?? 0,
      cruise_type: r.cruise_type || '',
      deck: r.deck || '',
      customer_id: r.customer_id,
      notes: noteParts.join(' — '),
    });
  };

  // Notes for one row: the newest typed call note (earlier ones behind a
  // toggle), then the lead note. Empty when nobody has written anything.
  const noteLinesFor = (r) => {
    const calls = callNotes[r.customer_id] || [];
    const shown = openNotes[r.customer_id] ? calls : calls.slice(0, 1);
    const lines = shown.map(f => ({
      key: f.id,
      label: 'Call note',
      meta: `${callerLabel(f.called_by)} · ${relTime(f.created_at)} · ${outcomeMeta(f.outcome).label}`,
      title: new Date(f.created_at).toLocaleString('en-IN'),
      text: f.note.trim(),
    }));
    const lead = leadNotes[r.customer_id];
    if (lead) {
      const who = lead.note_updated_by || lead.added_by;
      const when = lead.note_updated_at || lead.created_at;
      lines.push({
        key: `lead-${lead.id}`,
        label: 'Lead note',
        meta: [who && callerLabel(who), relTime(when), lead.status === 'Closed' && 'lead closed'].filter(Boolean).join(' · '),
        title: when ? new Date(when).toLocaleString('en-IN') : '',
        text: lead.note.trim(),
      });
    }
    return lines;
  };

  const missingFn = error && (error.code === 'PGRST202' || error.code === '42883');

  const cellBase = { padding: '11px 12px', borderBottom: '1px solid var(--color-border-subtle)', fontSize: '0.82rem', verticalAlign: 'top' };
  const thBase = { padding: '8px 12px', textAlign: 'left', fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--color-ink-muted)', background: 'var(--color-sunken)', borderBottom: '2px solid #C9A84C88', whiteSpace: 'nowrap' };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <button className="back-btn" onClick={() => onNavigate('home')}>← Home</button>
        <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 800, color: NAVY, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <Hourglass size={18} strokeWidth={1.75} aria-hidden="true" />
          Incomplete Bookings
          {rows && <span style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--color-ink-muted)' }}>({rows.length})</span>}
        </h2>
        <div style={{ flex: 1 }} />
        <button className="btn btn-navy btn-sm" onClick={fetchRows} disabled={loading}>
          <RefreshCw size={15} strokeWidth={1.75} aria-hidden="true" />{loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      <p style={{ margin: '0 0 14px', fontSize: '0.78rem', color: 'var(--color-ink-muted)', maxWidth: 720 }}>
        People who started booking online but never completed payment — newest first. Log every call
        so nobody rings twice; convert a still-interested lead into a pending booking (no tables are
        held until a manager seats them). Converted leads drop off this list automatically.
      </p>

      {justLogged && (
        <div style={{ background: '#F0FDF4', border: '1px solid #86EFAC', borderRadius: 10, padding: '10px 16px', maxWidth: 720, marginBottom: 12, fontSize: '0.8rem', color: '#15803D', fontWeight: 600 }}>
          {justLogged}{justLogged.startsWith('Call logged') ? ' Wrong outcome? Log another call — the newest entry is what\'s shown.' : ''}
        </div>
      )}

      {dupPairs.length > 0 && (
        <div style={{ background: '#FEE2E2', border: '1px solid #FCA5A5', borderLeft: '4px solid #DC2626', borderRadius: 10, padding: '14px 18px', maxWidth: 780, marginBottom: 14 }}>
          <div style={{ fontWeight: 800, color: '#991B1B', fontSize: '0.88rem', display: 'flex', alignItems: 'center', gap: 7, marginBottom: 2 }}>
            <AlertTriangle size={16} strokeWidth={2} aria-hidden="true" />
            Possible duplicate bookings ({dupPairs.length})
          </div>
          <div style={{ fontSize: '0.74rem', color: '#7F1D1D', marginBottom: 6 }}>
            Same online guest, same cruise date, same cruise type, more than one live booking.
            Usually the pending internal one should be cancelled — check with the guest first.
          </div>
          {dupPairs.map(p => (
            <div key={p.key} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '8px 0', borderTop: '1px solid #FECACA' }}>
              <div style={{ flex: 1, fontSize: '0.8rem', color: '#7F1D1D', minWidth: 0 }}>
                {[p.a, p.b].map(b => (
                  <div key={b.id} style={{ display: 'flex', gap: 10, flexWrap: 'wrap', padding: '2px 0' }}>
                    <span style={{ fontWeight: 700 }}>{b.booking_name}</span>
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>{b.date} · {b.cruise_type}{b.deck ? ` · ${b.deck}` : ''}</span>
                    <span style={{ fontWeight: 600 }}>{channelLabel(b)} · {b.payment_status || '—'}</span>
                  </div>
                ))}
              </div>
              <button
                onClick={() => dismissPair(p)}
                disabled={dismissMissing || dismissing === p.key}
                title={dismissMissing ? 'Run db/duplicate_dismissals.sql in Supabase to enable this' : 'Hide this pair permanently'}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 11px', borderRadius: 8, border: '1.5px solid #FCA5A5', background: '#fff', color: '#991B1B', fontWeight: 700, fontSize: '0.74rem', cursor: dismissMissing ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap', opacity: dismissMissing || dismissing === p.key ? 0.55 : 1, flexShrink: 0 }}>
                <EyeOff size={13} strokeWidth={1.75} aria-hidden="true" />
                {dismissing === p.key ? 'Saving…' : 'Not a duplicate'}
              </button>
            </div>
          ))}
          {dismissError && (
            <div style={{ fontSize: '0.76rem', color: '#991B1B', marginTop: 6 }}>Could not dismiss: {dismissError}</div>
          )}
          {dismissMissing && (
            <div style={{ fontSize: '0.72rem', color: '#7F1D1D', marginTop: 6 }}>
              Dismissing needs a one-time setup: run <code>db/duplicate_dismissals.sql</code> in the Supabase SQL editor, then refresh.
            </div>
          )}
        </div>
      )}

      {dismissed.length > 0 && (
        <div style={{ maxWidth: 780, marginBottom: 14 }}>
          <button
            onClick={() => setShowDismissed(v => !v)}
            aria-expanded={showDismissed}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 8px', borderRadius: 6, border: '1px solid var(--color-border)', background: '#fff', color: 'var(--color-ink-secondary)', fontWeight: 600, fontSize: '0.74rem', cursor: 'pointer' }}>
            {showDismissed
              ? <ChevronDown size={13} strokeWidth={1.75} aria-hidden="true" />
              : <ChevronRight size={13} strokeWidth={1.75} aria-hidden="true" />}
            Dismissed as not duplicates ({dismissed.length})
          </button>
          {showDismissed && (
            <div style={{ marginTop: 8, background: '#fff', border: '1px solid var(--color-border)', borderRadius: 10, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={thBase}>Pair</th>
                    <th style={thBase}>Dismissed by</th>
                    <th style={thBase}>When</th>
                  </tr>
                </thead>
                <tbody>
                  {dismissed.map(d => (
                    <tr key={d.id}>
                      <td style={{ ...cellBase, color: NAVY, fontWeight: 600 }}>{d.summary}</td>
                      <td style={{ ...cellBase, whiteSpace: 'nowrap', color: 'var(--color-ink-secondary)' }}>{callerLabel(d.dismissed_by)}</td>
                      <td style={{ ...cellBase, whiteSpace: 'nowrap', color: 'var(--color-ink-muted)' }} title={new Date(d.created_at).toLocaleString('en-IN')}>
                        {relTime(d.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ padding: '8px 12px', fontSize: '0.72rem', color: 'var(--color-ink-faint)', borderTop: '1px solid var(--color-border-subtle)' }}>
                Dismissals are permanent. A pair only reappears if a new booking joins it.
              </div>
            </div>
          )}
        </div>
      )}

      {missingFn && (
        <div style={{ background: '#FFFBEB', border: '1px solid #FCD34D', borderLeft: '4px solid #F59E0B', borderRadius: 10, padding: '14px 18px', maxWidth: 720 }}>
          <div style={{ fontWeight: 700, color: '#92400E', fontSize: '0.88rem', marginBottom: 4 }}>Database setup needed</div>
          <div style={{ fontSize: '0.8rem', color: '#92400E' }}>
            Run <code>db/get_incomplete_checkouts.sql</code> (v2 — includes the call log) in the
            Supabase SQL editor, then refresh this page.
          </div>
        </div>
      )}
      {error && !missingFn && (
        <div style={{ background: '#FFF1F2', border: '1px solid #FCA5A5', borderRadius: 10, padding: '14px 18px', maxWidth: 720, fontSize: '0.82rem', color: '#991B1B' }}>
          Could not load: {error.message}
        </div>
      )}

      {rows && rows.length === 0 && !error && (
        <div style={{ background: '#fff', border: '1px solid var(--color-border)', borderRadius: 12, padding: '32px 20px', textAlign: 'center', color: 'var(--color-ink-muted)', fontSize: '0.88rem' }}>
          No incomplete bookings — everyone who started either finished or is excluded.
        </div>
      )}

      {rows && rows.length > 0 && (
        <div style={{ background: '#fff', border: '1px solid var(--color-border)', borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1020 }}>
              <thead>
                <tr>
                  <th style={thBase}>Guest</th>
                  <th style={thBase}>How far they got</th>
                  <th style={thBase}>Looking at</th>
                  <th style={thBase}>Last call</th>
                  <th style={thBase}>When</th>
                  <th style={thBase}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => {
                  const stage = STAGES[r.stage] || STAGES.logged_in;
                  const noteLines = noteLinesFor(r);
                  const earlier = (callNotes[r.customer_id] || []).length - 1;
                  // With a note strip below, the divider moves to the strip's row.
                  const cell = noteLines.length ? { ...cellBase, borderBottom: 'none' } : cellBase;
                  return (
                    <Fragment key={r.customer_id}>
                    <tr>
                      <td style={{ ...cell, minWidth: 180 }}>
                        <div style={{ fontWeight: 700, color: NAVY }}>{r.full_name || 'Unknown name'}</div>
                        {r.phone && (
                          <div style={{ marginTop: 3, fontSize: '0.76rem', color: 'var(--color-ink-secondary)', display: 'flex', alignItems: 'center', gap: 5 }}>
                            <Phone size={12} strokeWidth={1.75} aria-hidden="true" />
                            <a href={`tel:${r.phone}`} style={{ color: 'inherit', textDecoration: 'none', fontVariantNumeric: 'tabular-nums' }}>{r.phone}</a>
                          </div>
                        )}
                        {r.email && (
                          <div style={{ marginTop: 2, fontSize: '0.76rem', color: 'var(--color-ink-secondary)', display: 'flex', alignItems: 'center', gap: 5, wordBreak: 'break-all' }}>
                            <Mail size={12} strokeWidth={1.75} aria-hidden="true" />{r.email}
                          </div>
                        )}
                        {r.maybe_booked_by_phone && (
                          <div style={{ marginTop: 5 }}>
                            {chip('May have booked by phone', { color: '#4B5563', bg: '#F3F4F6', border: '#D1D5DB' })}
                          </div>
                        )}
                      </td>
                      <td style={{ ...cell, whiteSpace: 'nowrap' }}>
                        {chip(stage.label, stage)}
                        {r.hold_live && (
                          <div style={{ marginTop: 5 }}>
                            {chip('Holding seats now', { color: '#15803D', bg: '#F0FDF4', border: '#86EFAC' })}
                          </div>
                        )}
                      </td>
                      <td style={{ ...cell, minWidth: 180 }}>
                        {r.cruise_type ? (
                          <>
                            <div style={{ fontWeight: 600, color: 'var(--color-ink-secondary)' }}>
                              {r.cruise_type}{r.deck ? ` · ${r.deck}` : ''}
                            </div>
                            <div style={{ marginTop: 2, fontSize: '0.76rem', color: 'var(--color-ink-muted)', fontVariantNumeric: 'tabular-nums' }}>
                              {holdDateLabel(r.hold_date)}{partyLabel(r) ? ` · ${partyLabel(r)}` : ''}
                            </div>
                          </>
                        ) : (
                          <span style={{ color: 'var(--color-ink-faint)' }}>—</span>
                        )}
                      </td>
                      <td style={{ ...cell, minWidth: 150 }}>
                        {r.last_call_at ? (
                          <>
                            {chip(outcomeMeta(r.last_call_outcome).label, outcomeMeta(r.last_call_outcome))}
                            <div style={{ marginTop: 4, fontSize: '0.72rem', color: 'var(--color-ink-muted)' }}>
                              {callerLabel(r.last_call_by)} · {relTime(r.last_call_at)}
                              {r.call_count > 1 ? ` · ${r.call_count} calls` : ''}
                            </div>
                          </>
                        ) : (
                          <span style={{ fontSize: '0.76rem', color: 'var(--color-ink-faint)' }}>Never called</span>
                        )}
                      </td>
                      <td style={{ ...cell, whiteSpace: 'nowrap' }}>
                        <span title={r.last_activity ? new Date(r.last_activity).toLocaleString('en-IN') : ''}
                          style={{ fontWeight: 700, color: relColor(r.last_activity), fontSize: '0.8rem' }}>
                          {relTime(r.last_activity)}
                        </span>
                      </td>
                      <td style={{ ...cell, whiteSpace: 'nowrap' }}>
                        <button onClick={() => { setJustLogged(''); setLogLead(r); }}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 11px', borderRadius: 8, border: '1.5px solid var(--color-border-strong)', background: '#fff', color: NAVY, fontWeight: 700, fontSize: '0.76rem', cursor: 'pointer', marginRight: 6 }}>
                          <PhoneCall size={13} strokeWidth={1.75} aria-hidden="true" />Log call
                        </button>
                        {leadByCustomer[r.customer_id] ? (
                          <span title="Recorded on the Enquiries screen"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 10px', borderRadius: 8, background: '#F0FDF4', color: '#15803D', border: '1px solid #86EFAC', fontWeight: 700, fontSize: '0.72rem', marginRight: 6, whiteSpace: 'nowrap' }}>
                            <CalendarPlus size={13} strokeWidth={1.75} aria-hidden="true" />
                            Lead added · {isoToDisplay(leadByCustomer[r.customer_id].preferred_date)}
                          </span>
                        ) : (
                          <button onClick={() => { setJustLogged(''); setAddLead(r); }}
                            title="Interested, but for a date online booking is not open for yet"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 11px', borderRadius: 8, border: '1.5px solid var(--color-border-strong)', background: '#fff', color: NAVY, fontWeight: 700, fontSize: '0.76rem', cursor: 'pointer', marginRight: 6 }}>
                            <CalendarPlus size={13} strokeWidth={1.75} aria-hidden="true" />Add lead
                          </button>
                        )}
                        <button onClick={() => convertLead(r)}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 11px', borderRadius: 8, border: 'none', background: NAVY, color: '#fff', fontWeight: 700, fontSize: '0.76rem', cursor: 'pointer' }}>
                          <UserPlus size={13} strokeWidth={1.75} aria-hidden="true" />Convert
                        </button>
                      </td>
                    </tr>
                    {noteLines.length > 0 && (
                      <tr>
                        <td colSpan={6} style={{ padding: '0 12px 11px', borderBottom: cellBase.borderBottom }}>
                          <NoteStrip
                            lines={noteLines}
                            toggle={earlier > 0 ? {
                              label: openNotes[r.customer_id] ? 'Hide earlier' : `${earlier} earlier`,
                              open: !!openNotes[r.customer_id],
                              onClick: () => setOpenNotes(o => ({ ...o, [r.customer_id]: !o[r.customer_id] })),
                            } : null}
                          />
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {rows && rows.some(r => r.stage === 'paid_no_seat') && (
        <p style={{ marginTop: 10, fontSize: '0.74rem', color: '#991B1B', maxWidth: 720 }}>
          Guests marked "Paid — seating failed" have a verified payment with no seat — they need a
          refund or manual seating. These also appear as booking exceptions with a WhatsApp alert.
        </p>
      )}

      <p style={{ marginTop: 10, fontSize: '0.72rem', color: 'var(--color-ink-faint)', maxWidth: 720 }}>
        "May have booked by phone" means an existing booking shares this phone number — it is shown,
        not hidden, because numbers are often shared between family members or agents.
      </p>

      {addLead && (
        <AddLeadModal
          lead={addLead}
          cruiseSettings={cruiseSettings}
          onClose={() => setAddLead(null)}
          onSaved={() => {
            const name = addLead.full_name || 'lead';
            setAddLead(null);
            setJustLogged(`Lead added for ${name} — it is now on the Enquiries screen.`);
            fetchRows();
          }}
        />
      )}

      {logLead && (
        <LogCallModal
          lead={logLead}
          onClose={() => setLogLead(null)}
          onSaved={() => {
            const name = logLead.full_name || 'lead';
            setLogLead(null);
            setJustLogged(`Call logged for ${name}.`);
            fetchRows();
          }}
        />
      )}
    </div>
  );
}
